import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from 'tss-react/mui';
import {useIntl} from 'react-intl';

const useStyles = makeStyles()((theme) => ({
  wrapper: {
    width: '100%',
  },
  canvasBox: {
    position: 'relative',
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    height: 180,
    overflow: 'hidden',
  },
  canvas: {
    width: '100%',
    height: '100%',
    display: 'block',
    touchAction: 'none',
    cursor: 'crosshair',
  },
  placeholder: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    color: theme.palette.text.disabled,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.pxToRem(16),
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: 8,
  },
  clearButton: {
    background: 'none',
    border: 'none',
    color: theme.palette.primary.main,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.pxToRem(15),
    cursor: 'pointer',
    padding: 0,
  },
}));

const STROKE_WIDTH = 2.5;

const SignaturePad = forwardRef(({onChange}, ref) => {
  const {classes, theme} = useStyles();
  const intl = useIntl();
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const lastMidRef = useRef(null);
  const hasInkRef = useRef(false);
  const [isEmpty, setIsEmpty] = useState(true);

  const prepareCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ratio = window.devicePixelRatio || 1;
    const width = Math.round(canvas.clientWidth * ratio);
    const height = Math.round(canvas.clientHeight * ratio);

    // Resizing wipes the bitmap, so only touch it when the size really changed, and carry the strokes over.
    if (width && height && (canvas.width !== width || canvas.height !== height)) {
      const snapshot = document.createElement('canvas');
      const hadContent = canvas.width > 0 && canvas.height > 0;

      if (hadContent) {
        snapshot.width = canvas.width;
        snapshot.height = canvas.height;
        snapshot.getContext('2d').drawImage(canvas, 0, 0);
      }

      canvas.width = width;
      canvas.height = height;

      if (hadContent) {
        canvas.getContext('2d').drawImage(snapshot, 0, 0, width, height);
      }
    }

    const context = canvas.getContext('2d');
    // setTransform, not scale: scale compounds every time this runs and would double the stroke size.
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.lineWidth = STROKE_WIDTH;
    context.strokeStyle = theme.palette.text.primary;
    contextRef.current = context;
  }, [theme]);

  useEffect(() => {
    prepareCanvas();
    window.addEventListener('resize', prepareCanvas);
    return () => window.removeEventListener('resize', prepareCanvas);
  }, [prepareCanvas]);

  const getPoint = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {x: event.clientX - rect.left, y: event.clientY - rect.top};
  };

  const markInk = () => {
    if (hasInkRef.current) return;
    hasInkRef.current = true;
    setIsEmpty(false);
    if (onChange) onChange(false);
  };

  // Curve from the previous midpoint to the current one through the shared point. Consecutive segments
  // share an endpoint, so the stroke is continuous instead of leaving a gap on every other half-segment.
  const drawTo = (point) => {
    const context = contextRef.current;
    const last = lastPointRef.current;
    if (!context || !last) return;

    const mid = {x: (last.x + point.x) / 2, y: (last.y + point.y) / 2};

    context.beginPath();
    context.moveTo(lastMidRef.current.x, lastMidRef.current.y);
    context.quadraticCurveTo(last.x, last.y, mid.x, mid.y);
    context.stroke();

    lastMidRef.current = mid;
    lastPointRef.current = point;
  };

  const handlePointerDown = (event) => {
    event.preventDefault();
    canvasRef.current.setPointerCapture(event.pointerId);

    const point = getPoint(event);
    const context = contextRef.current;
    drawingRef.current = true;
    lastPointRef.current = point;
    lastMidRef.current = point;

    // A dot: a tap with no movement still has to leave a mark.
    context.beginPath();
    context.arc(point.x, point.y, STROKE_WIDTH / 2, 0, Math.PI * 2);
    context.fillStyle = context.strokeStyle;
    context.fill();
    markInk();
  };

  const handlePointerMove = (event) => {
    if (!drawingRef.current) return;
    event.preventDefault();

    // Coalesced events recover the points the browser batched between frames — fast strokes stay smooth.
    const coalesced = event.nativeEvent.getCoalescedEvents ? event.nativeEvent.getCoalescedEvents() : [];

    if (coalesced.length) {
      coalesced.forEach((item) => drawTo(getPoint(item)));
    } else {
      drawTo(getPoint(event));
    }

    markInk();
  };

  const handlePointerUp = (event) => {
    if (!drawingRef.current) return;

    drawTo(getPoint(event));

    // The curve always stops at a midpoint — close the tail so the stroke ends where the finger did.
    const context = contextRef.current;
    context.beginPath();
    context.moveTo(lastMidRef.current.x, lastMidRef.current.y);
    context.lineTo(lastPointRef.current.x, lastPointRef.current.y);
    context.stroke();

    drawingRef.current = false;
    lastPointRef.current = null;
    lastMidRef.current = null;
  };

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    contextRef.current.clearRect(0, 0, canvas.width / ratio, canvas.height / ratio);
    hasInkRef.current = false;
    setIsEmpty(true);
    if (onChange) onChange(true);
  }, [onChange]);

  // The canvas is mostly empty around the strokes. Cropping to the ink keeps the signature legible
  // once the document scales it down, instead of shrinking a mostly-transparent image.
  const toTrimmedDataURL = useCallback(() => {
    const canvas = canvasRef.current;
    const {width, height} = canvas;
    const {data} = canvas.getContext('2d').getImageData(0, 0, width, height);
    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (data[(y * width + x) * 4 + 3] === 0) continue;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }

    if (maxX < 0) return canvas.toDataURL('image/png');

    const padding = Math.round(4 * (window.devicePixelRatio || 1));
    const left = Math.max(0, minX - padding);
    const top = Math.max(0, minY - padding);
    const cropped = document.createElement('canvas');
    cropped.width = Math.min(width - 1, maxX + padding) - left + 1;
    cropped.height = Math.min(height - 1, maxY + padding) - top + 1;
    cropped.getContext('2d')
      .drawImage(canvas, left, top, cropped.width, cropped.height, 0, 0, cropped.width, cropped.height);

    return cropped.toDataURL('image/png');
  }, []);

  useImperativeHandle(ref, () => ({
    clear,
    isEmpty: () => isEmpty,
    resize: prepareCanvas,
    toDataURL: toTrimmedDataURL,
  }), [clear, isEmpty, prepareCanvas, toTrimmedDataURL]);

  return (
    <div className={classes.wrapper}>
      <div className={classes.canvasBox}>
        <canvas
          ref={canvasRef}
          className={classes.canvas}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          onPointerCancel={handlePointerUp}
        />
        {isEmpty && (
          <div className={classes.placeholder}>
            {intl.formatMessage({id: 'sign.pad.placeholder'})}
          </div>
        )}
      </div>
      <div className={classes.actions}>
        <button type='button' className={classes.clearButton} onClick={clear}>
          {intl.formatMessage({id: 'sign.pad.clear'})}
        </button>
      </div>
    </div>
  );
});

SignaturePad.displayName = 'SignaturePad';

SignaturePad.propTypes = {
  onChange: PropTypes.func,
};

export default SignaturePad;
